import {ChangeDetectionStrategy, Component, computed, effect, input, output} from "@angular/core";
import {AntallOppgaverPerBrukerModell, EnkelBrukerModell, OppgaveModell, TilgangsrolleMedlem, OppgavestatusKriterie, Sortering, Oppgavesorteringskolonne} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {toSignal} from "@angular/core/rxjs-interop";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {TranslateModule} from "@ngx-translate/core";
import {map} from "rxjs/operators";
import {sorterBrukerPåEpostEllerNavn} from "felles-ng2/modell/brukerModellHjelpere";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {OppgaveListevisningKompaktKomponent} from "web-ng2/moduler/oppgave/komponenter/oppgave-listevisning-kompakt/oppgave-listevisning-kompakt";
import {begrensTilVerdiMellom} from "felles-ng2/støttefunksjoner/tall";

const debug = initDebugger('OppgaverPopupKomponent');

/** Panel som viser oversikt over ikke-utførte oppgaver */
@Component({
    selector: 'oppgaver-popup',
    templateUrl: 'oppgaver-popup.html',
    styleUrl: 'oppgaver-popup.scss',
    standalone: true,
    imports: [AvatarKomponent, TranslateModule, MatIcon, MatIconButton, OppgaveListevisningKompaktKomponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OppgaverPopupKomponent {

    vedLukk = output<void>();

    brukersOppgaver = input.required<OppgaveModell[]>();
    antallOppgaverPerBruker = input.required<AntallOppgaverPerBrukerModell[]>();
    maksPopupHoeyde = input<number>(100);

    brukersOppgaverBegrensetTilMaksAntall = computed(() => this.brukersOppgaver().slice(0, this.maksAntallOppgaverAaVise()));
    harSkrivetilgang = toSignal(this.kontekst.autentisertBrukerSomStrøm().pipe(map(bruker => bruker.inneharTilgangsrolleINoenGruppe(TilgangsrolleMedlem.skrive))), {initialValue: false});
    /** Antall oppgaver vi kan vise uten å overgå angitt {@linkn #maksPopupHoeyde} */
    maksAntallOppgaverAaVise = computed(() => this.beregnMaksAntallOppgaverÅVise(this.maksPopupHoeyde()));
    /** Antall forfalte oppgaver til andre brukere */
    antallOppgaverTilknyttetAndre = computed(() => this.beregnAntallOppgaverTilknyttetAndre(this.antallOppgaverPerBruker()));

    constructor(private navigasjon: Navigasjon, private kontekst: BrukerkontekstTilbyder) {
    }

    protected gaaTilOppgave(oppgave: OppgaveModell) {
        this.navigasjon.gåTilOppgave(oppgave);
    }

    gaaTilListBrukersForfalteOppgaver(bruker: EnkelBrukerModell) {
        const epost = bruker.epost;
        this.navigasjon.gåTilOppgaveliste(
          {
              grupper: [],
              statuser: [OppgavestatusKriterie.forfalt],
              ansvarlige: [epost],
              ansvarligUtvalg: epost ? 'valgte' : 'mine',
              sortering: {kolonne: Oppgavesorteringskolonne.frist, retning: Sortering.synkende}
          }
        )
    }

    gaaTilListMineIkkeUtfoerteOppgaver() {
        this.navigasjon.gåTilOppgaveliste(
          {
              grupper: [],
              statuser: [OppgavestatusKriterie.forfalt, OppgavestatusKriterie.ikke_forfalt, OppgavestatusKriterie.uten_frist],
              ansvarlige: [],
              ansvarligUtvalg: 'mine',
              sortering: {kolonne: Oppgavesorteringskolonne.frist, retning: Sortering.synkende}
          }
        )
    }

    lukk() {
        this.vedLukk.emit();
    }

    // Hjelpemetoder

    private beregnMaksAntallOppgaverÅVise(maksPopupHøyde: number) {
        const høydeIPopupSomIkkeSkalBrukesTilOppgaveRader = 240;
        const ønsketMaksHøydeTilOppgaveTabell = maksPopupHøyde - høydeIPopupSomIkkeSkalBrukesTilOppgaveRader;
        let maks = Math.floor(ønsketMaksHøydeTilOppgaveTabell / 110);
        maks = begrensTilVerdiMellom(maks, 3, 6);
        return maks;
    }

    private beregnAntallOppgaverTilknyttetAndre(liste: AntallOppgaverPerBrukerModell[]) {
        return liste
          .filter(oppgaverOgBruker => oppgaverOgBruker.bruker?.id != this.kontekst.autentisertBruker().bruker.id)
          .filter(utenomInnloggetBruker => utenomInnloggetBruker.antallForfalte > 0)
          .sort((a, b) => sorterBrukerPåEpostEllerNavn(a.bruker, b.bruker));
    }
}
